// components/portal/tables/properties/PropertiesTable.tsx
'use client';

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { EntityDeletionModal } from '@/components/entities/EntityDeletionModal';
import { createScopedOrgMemberToolbarExtras } from '@/components/entities/tables/ScopedOrgMemberToolbarExtras';
import { useScopedOrgMemberFilters } from '@/components/entities/tables/useScopedOrgMemberFilters';
import { ViewModeGraphicToggle, type InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { supportsInventoryViewModeToggle } from '@/components/portal/inventoryViewModeCapabilities';
import { Trash2, Briefcase, Grid2x2, Landmark, Percent, SquareDashed, Tag, Users } from '@/components/ui/lucide-memo';
import { EntityRowAction, InventoryEntityType, LeasingPropertyDTO, LeasingPropertyFilters, PropertySortField } from '#types/types';
import { Table, TableColumnDef } from '../../../portal/Table';

const translations = {
  en: {
    alphabetical: 'Alphabetical',
    city: 'City',
    unitCount: 'Unit Count',
    owner: 'Owner',
    occupancyRate: 'Occupancy Rate',
    newProperty: 'New Property',
    assetHeader: 'Asset',
    ownerHeader: 'Owner',
    typeHeader: 'Type',
    managers: 'Managers',
    unitsHeader: 'Units',
    occupancyHeader: 'Occupation',
    vacantHeader: 'Vacant (m2)',
    deleteAsset: 'Delete asset',
  },
  fr: {
    alphabetical: 'Alphabetique',
    city: 'Ville',
    unitCount: 'Nb unites',
    owner: 'Proprietaire',
    occupancyRate: 'Taux occupation',
    newProperty: 'Nouvel actif',
    assetHeader: 'Actif',
    ownerHeader: 'Proprietaire',
    typeHeader: 'Type',
    managers: 'Gestionnaires',
    unitsHeader: 'Unites',
    occupancyHeader: 'Occupation',
    vacantHeader: 'Vacant (m2)',
    deleteAsset: "Supprimer l'actif",
  },
};

interface PropertiesTableProps {
  initialFilters?: Partial<LeasingPropertyFilters>;
  /** Hide the toolbar search when the caller renders its own above the table. */
  hideSearch?: boolean;
  lockedFilters?: Partial<LeasingPropertyFilters>;
  /** Restrict the table to these property ids (used by the Assets region→type folder browser). */
  scopedIds?: string[];
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  showDefaultToolbarSelectors?: boolean;
  renderCustomToolbarHeader?: () => React.ReactNode;
  onCreateClick?: () => void;
  viewModeVisual?: InventoryVisualMode;
  onViewModeVisualChange?: (mode: InventoryVisualMode) => void;
  preview?: boolean;
}

export const PropertiesTable = React.memo(({
  initialFilters,
  hideSearch,
  lockedFilters,
  scopedIds,
  assetLedgerTitle = 'All assets',
  assetLedgerBreadcrumb = 'Inventory / Assets',
  showDefaultToolbarSelectors = true,
  renderCustomToolbarHeader,
  onCreateClick,
  viewModeVisual,
  onViewModeVisualChange,
  preview = false,
}: PropertiesTableProps) => {
  const { kernel } = useKernel();
  const userContext = useKernelStore((state) => state.userContext);
  const orgRegistry = useKernelStore((state) => state.inventory.Organisation.registry);
  const propertyLastActivity = useKernelStore((state) => state.inventory.Property.lastActivity);
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const [visiblePropertyIds, setVisiblePropertyIds] = useState<string[]>([]);
  const [propertyToDelete, setPropertyToDelete] = useState<LeasingPropertyDTO | null>(null);
  const [localViewModeVisual, setLocalViewModeVisual] = useState<InventoryVisualMode>('table');
  const resolvedViewModeVisual = viewModeVisual ?? localViewModeVisual;
  const handleViewModeVisualChange = useCallback((mode: InventoryVisualMode) => {
    onViewModeVisualChange?.(mode);
    if (!onViewModeVisualChange) {
      setLocalViewModeVisual(mode);
    }
  }, [onViewModeVisualChange]);
  const handleVisibleIdsChange = useCallback((ids: string[]) => setVisiblePropertyIds(ids), []);
  const handleCloseDeleteModal = useCallback(() => setPropertyToDelete(null), []);

  const currentOrg = userContext.organisationId ? orgRegistry[userContext.organisationId] : null;
  const shouldSkipDefaultOrganisationFilter =
    userContext.isFaterAdmin ||
    userContext.isFaterOrgMember ||
    currentOrg?.name?.trim().toLowerCase() === 'fater';
  const [tableFilters, setTableFilters] = useState<Partial<LeasingPropertyFilters>>(() => ({
    ...(initialFilters || {}),
    ...(lockedFilters || {}),
    owner: lockedFilters?.owner ?? initialFilters?.owner,
    managerId: lockedFilters?.managerId ?? initialFilters?.managerId,
    sortBy: lockedFilters?.sortBy ?? initialFilters?.sortBy ?? 'UNIT_COUNT',
    sortDir: lockedFilters?.sortDir ?? initialFilters?.sortDir ?? 'desc',
    organisationId:
      lockedFilters?.organisationId ??
      initialFilters?.organisationId ??
      (shouldSkipDefaultOrganisationFilter ? undefined : currentOrg?.id ?? undefined),
  }));
  const {
    selectedOrgId,
    activeOrgId,
    selectedUser,
    hasDefaultToolbarSelectors,
    showOrganisationSelector,
    showUserSelector,
    handleOrgSelect,
    handleUserSelect,
  } = useScopedOrgMemberFilters<LeasingPropertyFilters>({
    tableFilters,
    setTableFilters,
    lockedFilters,
    userFilterKey: 'managerId',
    showDefaultToolbarSelectors,
  });

  const sortOptions = useMemo<{ label: string; field: PropertySortField }[]>(() => [
    { label: t.alphabetical, field: 'NAME' },
    { label: t.city, field: 'CITY' },
    { label: t.unitCount, field: 'UNIT_COUNT' },
    { label: t.owner, field: 'OWNER' },
    { label: t.occupancyRate, field: 'OCCUPANCY' },
  ], [t]);

  const columns = useMemo<TableColumnDef[]>(() => [
    { id: 'property', icon: Landmark, minWidth: 200, header: t.assetHeader },
    { id: 'owner', icon: Briefcase, minWidth: 100, header: t.ownerHeader },
    { id: 'type', icon: Tag, minWidth: 100, header: t.typeHeader },
    { id: 'managers', icon: Users, minWidth: 100, header: t.managers },
    { id: 'units', icon: Grid2x2, minWidth: 80, header: t.unitsHeader, align: 'right' },
    { id: 'occupancy', icon: Percent, minWidth: 60, header: t.occupancyHeader, align: 'right' },
    { id: 'vacant', icon: SquareDashed, minWidth: 60, header: t.vacantHeader, align: 'right' },
    { id: 'actions', minWidth: 80, header: '', align: 'right' },
  ], [t]);

  useEffect(() => {
    const k = kernel.current;
    if (!k || visiblePropertyIds.length === 0) return;

    const inventory = k.getInventory();
    const propertyRegistry = inventory.Property.registry;
    const portfolioRegistry = inventory.Portfolio.registry;

    const portfolioIdsToFetch = new Set<string>();

    visiblePropertyIds.forEach((propertyId) => {
      const property = propertyRegistry[propertyId];
      if (!property) return;
      if (property.portfolioId && !portfolioRegistry[property.portfolioId]) {
        portfolioIdsToFetch.add(property.portfolioId);
      }
    });

    if (portfolioIdsToFetch.size > 0) {
      void k.ensureInventoryIds('Portfolio', Array.from(portfolioIdsToFetch));
    }
  }, [kernel, propertyLastActivity, visiblePropertyIds]);

  const canDeleteProperty = useCallback((property: LeasingPropertyDTO) => {
    if (!property) return false;
    if (userContext.isFaterAdmin) return true;
    if (!userContext.isOrganisationAdmin) return false;
    if (!userContext.organisationId || !property.portfolioOrganisationId) return false;
    return userContext.organisationId === property.portfolioOrganisationId;
  }, [
    userContext.isFaterAdmin,
    userContext.isOrganisationAdmin,
    userContext.organisationId,
  ]);

  const rowActions = useMemo<EntityRowAction<LeasingPropertyDTO>[]>(() => [
    {
      id: 'delete-property',
      label: t.deleteAsset,
      icon: <Trash2 size={13} strokeWidth={2} />,
      destructive: true,
      visible: canDeleteProperty,
      onClick: async (property) => {
        setPropertyToDelete(property);
      },
    },
  ], [canDeleteProperty, t.deleteAsset]);

  const handleConfirmDeleteEntity = useCallback(async (_entityType: InventoryEntityType, entityId: string) => {
    const k = kernel.current;
    if (!k || !entityId) {
      throw new Error('Deletion context unavailable.');
    }
    await k.deleteEntity('Property', entityId);
    setPropertyToDelete(null);
  }, [kernel]);

  const canShowViewModeToggle =
    hasDefaultToolbarSelectors && supportsInventoryViewModeToggle('Property');
  const composedToolbarHeader = useCallback(() => {
    if (!canShowViewModeToggle && !renderCustomToolbarHeader) return null;
    return (
      <>
        {canShowViewModeToggle && (
          <ViewModeGraphicToggle
            mode={resolvedViewModeVisual}
            onModeChange={handleViewModeVisualChange}
            className={renderCustomToolbarHeader ? 'mr-1' : undefined}
          />
        )}
        {renderCustomToolbarHeader?.()}
      </>
    );
  }, [
    canShowViewModeToggle,
    handleViewModeVisualChange,
    renderCustomToolbarHeader,
    resolvedViewModeVisual,
  ]);
  const effectiveToolbarHeader =
    canShowViewModeToggle || renderCustomToolbarHeader ? composedToolbarHeader : undefined;

  const toolbarExtras = useMemo(() => createScopedOrgMemberToolbarExtras({
    renderCustomToolbarHeader: effectiveToolbarHeader,
    hasDefaultToolbarSelectors,
    showOrganisationSelector,
    showUserSelector,
    selectedOrgId,
    selectedUser,
    activeOrgId: activeOrgId ?? undefined,
    onOrgSelect: handleOrgSelect,
    onUserSelect: handleUserSelect,
  }), [
    effectiveToolbarHeader,
    hasDefaultToolbarSelectors,
    showOrganisationSelector,
    showUserSelector,
    selectedOrgId,
    selectedUser,
    activeOrgId,
    handleOrgSelect,
    handleUserSelect,
  ]);

  // Stable identity is load-bearing: Table hands renderRow to a memoized TableRowWrapper per row,
  // so an inline arrow here re-renders every visible row on any PropertiesTable render.
  const renderPropertyRow = useCallback((
    property: LeasingPropertyDTO,
    _isExpanded: boolean,
    _toggle: () => void,
    currentColumns?: TableColumnDef[],
  ) => (
    <div className="flex w-full flex-none flex-col">
      <EntityView
        key={property.id}
        type="Property"
        entityId={property.id}
        variant="row"
        columns={currentColumns}
        rowActions={rowActions}
      />
    </div>
  ), [rowActions]);

  return (
    <>
      <Table
      hideSearch={hideSearch}
        entityType="Property"
        preview={preview}
        scopedIds={scopedIds}
        initialFilters={tableFilters}
        lockedFilters={lockedFilters}
        columns={columns}
        assetLedgerTitle={assetLedgerTitle}
        assetLedgerBreadcrumb={assetLedgerBreadcrumb}
        sortOptions={sortOptions}
        onCreateClick={onCreateClick}
        createButtonLabel={t.newProperty}
        onVisibleItemIdsChange={handleVisibleIdsChange}
        renderToolbarExtras={toolbarExtras}
        // The row no longer expands: expanding it used to unfold the asset page inline, and that
        // page is gone. An asset is reference data here, so the table is a list of rows and the
        // work happens in Source.
        renderRow={renderPropertyRow}
      />

      <EntityDeletionModal
        isOpen={Boolean(propertyToDelete)}
        onClose={handleCloseDeleteModal}
        entityType="Property"
        entityId={propertyToDelete?.id ?? ''}
        entityLabel={propertyToDelete?.name ?? null}
        stringToTypeToConfirm={(propertyToDelete?.name || propertyToDelete?.id || '').trim()}
        onSubmit={handleConfirmDeleteEntity}
      />
    </>
  );
});

PropertiesTable.displayName = 'PropertiesTable';
